import {
  MotionBox as Box,
  MotionGrid as Grid,
  MotionGridItem as GridItem,
  staggeredAppear,
  appearBelow,
  fadeIn,
} from "utils/motion";
import Text from "components/base/Text";
import Image from "components/base/Image";
// import ImageSequenceColorSection from 'components/sections/gsap/ImageSequenceColorSection';
import VideoPlayer from "components/sections/VideoPlayer";

const containerVariants = {
  hidden: { opacity: 1, scale: 0 },
  visible: {
    opacity: 1,
    scale: 1,
    transition: { delayChildren: 0.3, staggerChildren: 0.2 },
  },
};

const Item = ({ specs, idx, textType, gridColumn, noSeparator }) => (
  <>
    <GridItem
      display="flex"
      flexDirection={specs[idx].icon ? "column" : "row"}
      variants={appearBelow}
      alignItems="center"
      justifySelf="center"
      justifyContent="flex-end"
      gridColumn={gridColumn || null}
    >
      {specs[idx].icon && <Box pb={6}>{specs[idx].icon}</Box>}
      <Text
        type={textType || "title_xs"}
        color="white"
        px={2}
        maxWidth={specs[idx].icon ? "160px" : "184px"}
        textAlign="center"
        fontWeight="500"
      >
        {specs[idx].text}
      </Text>
    </GridItem>
    {idx !== specs.length - 1 && (
      <GridItem
        width={noSeparator ? 0 : "1px"}
        height={12}
        bg="white"
        alignSelf={specs[idx].icon ? "flex-end" : "center"}
      />
    )}
  </>
);

export default function HorizontalTechSpecs({ image, video, height, specs }) {
  const fullHeight = height || "100vh";

  return (
    <Box
      variants={staggeredAppear}
      width="100%"
      position="relative"
      minHeight={["xs", "xs", "65vh", fullHeight]}
    >
      <Box
        zIndex={1}
        display="flex"
        width="100%"
        minHeight={["xs", "xs", "65vh", fullHeight]}
        justifyContent="center"
        alignItems="flex-end"
      >
        <Box
          zIndex={1}
          width="100%"
          bg={[
            "linear-gradient(to bottom, rgba(0, 0, 0, 0) 0%, var(--chakra-colors-black-900) 100%)",
            null,
            "linear-gradient(to top, var(--chakra-colors-black-900) 0%, rgba(0, 0, 0, 0))",
          ]}
          display="flex"
          pt="10%"
          justifyContent="center"
        >
          {/*BIG DESKTOP */}
          <Grid
            display={[
              "none",
              null,
              specs.length > 4 ? null : "grid",
              "grid",
              "grid",
            ]}
            width="auto"
            px={6}
            pb="xxl"
            templateColumns={`repeat(${specs.length}, 1fr 1px)`}
            gap={[4, null, 4, 2, 8]}
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.9 }}
          >
            {specs.map((spec, idx) => (
              <Item specs={specs} idx={idx} key={`big-desktop-${idx}`} />
            ))}
          </Grid>

          {/* DESKTOP */}
          <Grid
            display={["none", null, specs.length > 4 ? "grid" : null, "none"]}
            width={"auto"}
            maxWidth={{ lg: "800px", xl: "1200px" }}
            mx="auto"
            px={6}
            pb={8}
            templateColumns={{
              base: "repeat(3, 1fr 1px)",
              xl: "repeat(5, 1fr 1px)",
            }}
            gap={4}
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.9 }}
          >
            {specs.slice(0, 3).map((spec, idx) => (
              <Item
                specs={specs.slice(0, 3)}
                idx={idx}
                key={`desktop-${idx}`}
              />
            ))}
            <GridItem
              colSpan={{ base: 5, xl: 3 }}
              display="flex"
              justifyContent="center"
            >
              <Grid templateColumns="1fr 1px 1fr" gap={4} w="66%">
                {specs.slice(3).map((spec, idx) => (
                  <Item
                    specs={specs.slice(3)}
                    idx={idx}
                    key={`desktop-2-${idx}`}
                  />
                ))}
              </Grid>
            </GridItem>
          </Grid>
        </Box>
      </Box>

      {/* MOBILE */}
      <Grid
        display={["grid", null, "none"]}
        variants={staggeredAppear}
        initial="hidden"
        alignItems="center"
        whileInView="visible"
        viewport={{ once: false, amount: 0.1 }}
        px={2}
        py={4}
        templateColumns="repeat(2, 1fr 1px)"
        rowGap={6}
      >
        {specs.map((spec, idx) => (
          <Item
            specs={specs}
            idx={idx}
            textType="title_xxs"
            key={`mobile-${idx}`}
            gridColumn={
              idx === specs.length - 1 && specs.length % 2 !== 0
                ? "1 / -1"
                : null
            }
            noSeparator={idx % 2 !== 0}
          />
        ))}
      </Grid>
      <Box
        width="100%"
        bg="black.900"
        minHeight={["xs", "xs", "65vh", fullHeight]}
        top={0}
        style={{ position: "absolute", overflow: "hidden" }}
      >
        {video ? (
          <VideoPlayer
            key={video.metadata?.video_id}
            stream={video.stream}
            metadata={video.metadata ?? {}}
            type="inline"
            options={
              video.options ?? {
                autoplay: true,
                muted: true,
                controls: false,
                loop: true,
              }
            }
            fullBackground
            boxStyle={{ height: "100%" }}
            height="100%"
            width="100%"
          />
        ) : (
          <Box variants={fadeIn} initial="hidden" animate="visible">
            <Image
              src={image}
              objectFit="cover"
              objectPosition="center"
              minHeight={["xs", "xs", "65vh", fullHeight]}
            />
          </Box>
        )}
      </Box>
    </Box>
  );
}
